import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { omitBy, isNil } from 'lodash'
import FineUploaderTraditional from 'fine-uploader-wrappers'
import { makeStyles } from '@mui/styles'

import { unassignFile, showFileContainer, hideFileContainer } from '@/redux/ducks/receipt/edit'

import { user } from 'ampersand-app'
import Carousel from '@/modules/base/components/carousel/carousel'
import ReceiptPreviewerItem from '@/modules/receipt/preview/item/receipt-previewer-item'
import ReceiptRemoveButton from '@/modules/receipt/preview/item/receipt-remove-button'
import ReceiptFileList from '@/modules/receipt/preview/file-list/receipt-preview-file-list'
import FileActionsSpeedDial from '@/components/controls/file-actions-speed-dial/file-actions-speed-dial'
import lang from '@/lang'
import ReceiptFileUploader from './uploader/receipt-file-uploader'
import { RECEIPT_TYPES, SUBSCRIPTION_MODULES, experimentalFeatures } from '@/modules/base/util/constants'
import { setFolder } from '@/redux/ducks/document/list'
import { Receipt, ReceiptList } from '@/modules/receipt/types'
// FLAG:useDocuAi — artifact switcher (original/XML/validation report/report/attachments) for e-invoices
// whose DMS document has children.
import InvoiceArtifactPreviewForFile from '@/modules/document/components/review/invoice-artifact-preview-for-file'

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    position: 'relative',
  },
  previewer: {
    '& .scrollbar-container': {
      outline: 'none',
    },

    '& .slick-slider, & .slick-list, & .slick-track': {
      minHeight: '100%',
      userSelect: 'unset',
    },
  },

  actionBar: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    right: 16,
    bottom: 16,

    '& > *': {
      marginBottom: 8,

      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
}))

interface ReceiptPreviewerProps {
  type?: string,
  date?: string,
  receipts: ReceiptList,
  accept: string[],
  dateFor: string,
  readOnly: boolean,
  disableOcr: boolean,
  hideDownload?: boolean,
  uploader: FineUploaderTraditional,
  onDeleteReceipt?: (file: Receipt) => void,
  onDeleteFile?: (file: Receipt) => void,
  onStartAnalyze?: (fileId: string, ocrData: any) => void,
  onUpdateReceiptMetaData?: (receipt: any, metaData: any) => void,
  // FLAG:useDocuAi — route the receipt upload through DMS→DocuAI (passed to ReceiptFileUploader).
  useDmsUpload?: boolean,
  onDmsUpload?: (files: unknown[]) => void,
  onSelectDocument?: (file: { id?: string | number }) => void,
}

const ReceiptPreviewer = (props: ReceiptPreviewerProps) => {
  const dispatch = useDispatch()
  const { isFileContainerOpen, filesToAssign } = useSelector(state => state.receipt.edit)

  const {
    type,
    uploader,
    accept,
    receipts = [],
    dateFor,
    readOnly,
    hideDownload,
    disableOcr,
    onDeleteReceipt,
    onDeleteFile,
    onUpdateReceiptMetaData,
    onStartAnalyze,
    useDmsUpload,
    onDmsUpload,
    onSelectDocument,
  } = props

  const acceptAttribute = accept.map(ext => `.${ext}`).join(', ')

  // ⚠ Counterpart to the location in `position-with-tax-category-list.js`: `useSelector` was inside
  // `if (type === EXPENSE)` and was therefore a conditional hook. Once `type` changes during the
  // component's lifetime — now that review follows the ledger of the selected contact role — the
  // hook count changes between renders and React dies in `areHookInputsEqual`.
  // Read optionally — see its counterpart: the selector now also runs where subscription details do
  // not exist (revenue host, tests without the slice).
  const subscriptionDetails = useSelector((state: any) => state?.subscription?.details)
  let hasReducedFeatures = false
  if (type === RECEIPT_TYPES.EXPENSE) {
    const expenseModule = (subscriptionDetails?.activeDetails || []).find(item => item.name.toUpperCase() === SUBSCRIPTION_MODULES.EXPENSES && item.used > 0)
    hasReducedFeatures = !!expenseModule?.limitations?.hasReducedFeatures
  }

  // eslint-disable-next-line
  const [activeSlide, setActiveSlide] = useState<number>(0)
  // FLAG:useDocuAi — gates the artifact switcher in renderItem below.
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const classes = useStyles(props)
  useEffect(() => {
    return () => {
      dispatch(hideFileContainer())
    }
  }, [])

  const carouselSettings = {
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: current => setActiveSlide(current),
  }

  const openFileContainer = event => {
    if (readOnly) return
    event.preventDefault()
    dispatch(showFileContainer())
    setFolder({})
  }

  const onUnassignFile = (file) => {
    if (readOnly) return
    dispatch(unassignFile(file))
    onDeleteFile(file)
  }

  const onUpdateReceipt = receipt => {
    if (onUpdateReceiptMetaData) {
      onUpdateReceiptMetaData(receipt, omitBy({ rotate: receipt.rotate, scale: receipt.scale }, isNil))
    }
  }

  const renderLegacyItem = (item, onDelete, key) => (
    <ReceiptPreviewerItem
      key={key}
      item={item}
      onDelete={onDelete}
      onUpdateReceipt={onUpdateReceipt}
      onStartAnalyze={onStartAnalyze}
      date={dateFor}
      disableDownload={hideDownload}
      downloadUrl={`${item.url}?token=${user.token}`}
      downloadLabel={lang.receipt.preview.download}
      isNotDataLink={true}
      disableOcr={disableOcr} />
  )

  // FLAG:useDocuAi — an e-invoice whose DMS document has children (extracted XML, KoSIT
  // validation report, report, embedded attachments) renders the artifact switcher here, the same
  // control the Documents drawer and the review page already use. Every other file — and every
  // file at all when the flag is off — keeps the verbatim legacy item, in the verbatim position
  // (no extra wrapper element, so the carousel slides are unchanged).
  const renderItem = (item, onDelete, key) => {
    if (!useDocuAiEnabled) return renderLegacyItem(item, onDelete, key)
    return (
      <InvoiceArtifactPreviewForFile
        key={key}
        fileId={item.fileId || item.id}
        // The legacy item is NOT mounted on an artifact slide, and it was the only thing carrying
        // „Dokument entfernen" — so an e-invoice could not be removed from the expense at all
        // (QA 2026-09-15). The very same button is handed to the artifact preview: same component,
        // same `onDelete` the legacy item gets one line below, same unlink-vs-delete decision.
        actions={<ReceiptRemoveButton item={item} onDelete={onDelete} />}>
        {renderLegacyItem(item, onDelete, undefined)}
      </InvoiceArtifactPreviewForFile>
    )
  }

  const renderCarousel = () => {
    return (
      <>
        {/* `draggable` is a Carousel PROP, not a Swiper setting — passed inside `settings` it
            reaches Swiper as an unknown option and does nothing, which is how this preview kept
            `allowTouchMove: true` despite saying otherwise. */}
        <Carousel
          settings={carouselSettings}
          draggable={false}
          customClass={classes.previewer}>
          {receipts.map(receipt => renderItem(receipt, onDeleteReceipt, receipt.fileId))}
          {filesToAssign.map(file => renderItem(file, onUnassignFile, file.id))}
        </Carousel>
        {!hasReducedFeatures && renderActionBar()}
      </>
    )
  }

  const renderActionBar = () => (
    <div className={classes.actionBar}>
      <FileActionsSpeedDial
        uploader={uploader}
        acceptAttribute={acceptAttribute}
        useDmsUpload={useDmsUpload}
        onDmsUpload={onDmsUpload}
        openFileContainer={openFileContainer} />
    </div>
  )

  const renderUploader = () => (
    <ReceiptFileUploader
      isFileContainerOpen={isFileContainerOpen}
      filesToAssign={filesToAssign}
      uploader={uploader}
      onOpenFileContainer={openFileContainer}
      useDmsUpload={useDmsUpload}
      onDmsUpload={onDmsUpload} />
  )

  const renderContent = () => {
    if (isFileContainerOpen) {
      return <ReceiptFileList files={filesToAssign} onSelectDocument={onSelectDocument} />
    }

    if (receipts.length + filesToAssign.length >= 1) {
      return renderCarousel()
    }

    return !readOnly && renderUploader()
  }

  return (<div className={classes.root}>{renderContent()}</div>)
}

export default ReceiptPreviewer
